import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FriendView } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageShell } from "../components/PageShell";
import { displayName } from "../components/profile-ui";
import { EmptyState, IdentityButton, LoadError, Pill, Row, Spinner } from "../components/social-ui";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/* ------------------------------------------------------------------ *
 * «کاربران مسدود» — everyone the caller has blocked, and the way back out.
 *
 * Reached from تنظیمات, where the row is shown WHETHER OR NOT anything is blocked: a
 * control that appears only once it has content cannot answer «where did they go?», which
 * is the question this screen exists for. Before it, the only door to آنبلاک was searching
 * the person's name in «افزودن» — the blocker still sees them there — so a block was a
 * one-way door for anyone who did not remember the name.
 *
 * Rows are the SAME `IdentityButton` the friends list uses, so tapping a name or avatar
 * opens that player's profile and its back button returns HERE (the store stamps the view
 * it was opened from). Unblocking is guarded by a confirm dialog; the list is a store
 * slice, so the row leaves as soon as the server confirms.
 * ------------------------------------------------------------------ */
export function BlockedUsers(): ReactNode {
  const blocked = useSession((s) => s.blocked);
  const error = useSession((s) => s.blockedError);
  const loadBlocked = useSession((s) => s.loadBlocked);
  const unblockUser = useSession((s) => s.unblockUser);
  const openUserProfile = useSession((s) => s.openUserProfile);
  const setView = useSession((s) => s.setView);

  const [confirming, setConfirming] = useState<FriendView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    void loadBlocked();
  }, [loadBlocked]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (msg: string): void => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const unblock = async (u: FriendView): Promise<void> => {
    setBusy(u.userId);
    try {
      await unblockUser(u.userId);
    } catch (err) {
      // The row stays put on failure — it only ever leaves on a server-confirmed unblock.
      showToast(faApiError(err));
    } finally {
      setBusy(null);
    }
  };

  // `blocked === null` means "not loaded yet", NOT "empty" — flashing «هنوز کسی را مسدود
  // نکرده‌ای» at somebody who has blocked people is the one thing this screen must not do.
  const body = (): ReactNode => {
    if (blocked === null)
      return error ? <LoadError onRetry={() => void loadBlocked()} /> : <Spinner />;
    if (blocked.length === 0) {
      return (
        <EmptyState icon="group" text={fa.blockedUsers.empty} hint={fa.blockedUsers.emptyHint} />
      );
    }
    return (
      <>
        <p className="m-0 font-caption text-caption text-on-surface-variant">
          {fa.blockedUsers.intro}
        </p>
        <ul className="m-0 flex list-none flex-col gap-sm p-0">
          {blocked.map((u) => (
            <li key={u.userId}>
              <Row>
                <IdentityButton user={u} onOpen={() => openUserProfile(u.userId)} showHandle />
                {/* A SIBLING of the identity button — never nested inside it. */}
                <div className="flex shrink-0 items-center gap-xs">
                  <Pill
                    variant="outline"
                    label={fa.blockedUsers.unblock}
                    disabled={busy === u.userId}
                    onClick={() => setConfirming(u)}
                  />
                </div>
              </Row>
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <PageShell title={fa.blockedUsers.title} icon="blocked" onBack={() => setView("settings")}>
      <div className="flex flex-col gap-sm">
        {body()}
        {/* A refresh that fails while rows are already on screen keeps the rows and says so. */}
        {blocked !== null && error ? (
          <p role="alert" className="m-0 font-caption text-caption text-error">
            {fa.friends.loadError}
          </p>
        ) : null}
      </div>

      {confirming ? (
        <ConfirmDialog
          title={fa.blockedUsers.unblockConfirmTitle}
          body={fa.blockedUsers.unblockConfirmBody(displayName(confirming))}
          confirmLabel={fa.blockedUsers.unblockConfirm}
          cancelLabel={fa.blockedUsers.unblockCancel}
          danger={false}
          onConfirm={() => {
            const target = confirming;
            setConfirming(null);
            void unblock(target);
          }}
          onCancel={() => setConfirming(null)}
        />
      ) : null}

      {toast ? (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-24 z-50 mx-auto w-max max-w-[90%] rounded-full bg-error px-4 py-2 font-body-md text-body-md text-on-error shadow-lg"
        >
          {toast}
        </div>
      ) : null}
    </PageShell>
  );
}
